// Typing-keyboard input for the Home Key piano.
//
// Everyone has the keyboard they type on, so it plays notes — the same
// virtual-piano convention as Number Sense and the Synth Lab (home row =
// white keys, the row above = the black keys between them), transposed so
// the home row starts on home (G3). The exact map:
//
//   A  = G3 (55, home)    W  = G#3 (56)
//   S  = A3 (57)          E  = A#3 (58)
//   D  = B3 (59)
//   F  = C4 (60)          T  = C#4 (61)
//   G  = D4 (62)          Y  = D#4 (63)
//   H  = E4 (64)
//   J  = F4 (65)          U  = F#4 (66)
//   K  = G4 (67, home)    O  = G#4 (68)
//   L  = A4 (69)          P  = A#4 (70)
//   ;  = B4 (71)
//   '  = C5 (72)
//
// The top part of the piano window (C#5–G5) has no typing keys — the
// pointer piano and a MIDI keyboard reach it. TYPING_KEY_BY_MIDI is
// exported so Piano can print each mapped letter as a key-cap.

import { useEffect, useRef } from "react";

export const TYPING_TO_MIDI = {
  a: 55, // G3 — home
  w: 56,
  s: 57,
  e: 58,
  d: 59,
  f: 60,
  t: 61,
  g: 62,
  y: 63,
  h: 64,
  j: 65,
  u: 66,
  k: 67, // G4 — home again
  o: 68,
  l: 69,
  p: 70,
  ";": 71,
  "'": 72, // C5
};

// midi → typing character, for the key-caps drawn on the piano.
export const TYPING_KEY_BY_MIDI = Object.fromEntries(
  Object.entries(TYPING_TO_MIDI).map(([ch, midi]) => [midi, ch])
);

// Shift held mid-press turns ";" into ":" (and "'" into '"') on keyup —
// normalize so the release still finds the note.
const keyOf = (e) => {
  const k = String(e.key).toLowerCase();
  if (k === ":") return ";";
  if (k === '"') return "'";
  return k;
};

const isTypingTarget = (el) =>
  el &&
  (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

// useTypingKeys(onPress, onRelease, enabled) — calls onPress(midi) on
// keydown and onRelease(midi) on keyup. Suppresses auto-repeat, ignores
// modifier chords and text fields, and releases everything still held on
// window blur / disable / unmount so no note sticks.
export default function useTypingKeys(onPress, onRelease, enabled = true) {
  const pressRef = useRef(onPress);
  pressRef.current = onPress;
  const releaseRef = useRef(onRelease);
  releaseRef.current = onRelease;

  useEffect(() => {
    if (!enabled) return undefined;
    const held = new Set();

    const down = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const midi = TYPING_TO_MIDI[keyOf(e)];
      if (midi == null || held.has(midi)) return;
      e.preventDefault();
      held.add(midi);
      pressRef.current(midi);
    };
    const up = (e) => {
      const midi = TYPING_TO_MIDI[keyOf(e)];
      if (midi == null || !held.has(midi)) return;
      held.delete(midi);
      releaseRef.current(midi);
    };
    const releaseAllHeld = () => {
      held.forEach((midi) => releaseRef.current(midi));
      held.clear();
    };

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", releaseAllHeld);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", releaseAllHeld);
      releaseAllHeld();
    };
  }, [enabled]);
}
